import { getFormProps, getInputProps, getSelectProps, SubmissionResult, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Icon } from '../ui/icon';
import { useEffect, useState } from 'react';
import { QuoteSession } from '#app/utils/get-a-quote/quote.server.ts';
import { QuoteForm } from './quote-form';
import { stateAbbreviations, usStates } from '#app/utils/misc.tsx';
import { Field, SelectField, SelectFieldItem } from '../forms';
import { StatusButton } from '../ui/status-button';
import { zipCodeRegex } from '#app/routes/resources+/get-a-quote.tsx';
import { HoneypotInputs } from 'remix-utils/honeypot/react';

export const AddressInfoSchema = z.object({
    streetAddress: z.string({ required_error: "Street address is required" })
        .min(3, "Street address seems too short")
        .max(150, "Street address is too long"),

    secondaryAddress: z.string()
        .max(100, "Street address line 2 is too long")
        .nullish(), // Optional field (Apt, Suite, etc.) - allows empty string, null, or undefined

    city: z.string({ required_error: "City is required" })
        .min(2, "City seems too short")
        .max(100, "City is too long"),

    state: z.enum(stateAbbreviations, { // Use z.enum with the abbreviations
        required_error: "Please select a state",
        invalid_type_error: "Invalid state selected"
    }),

    zip: z.string({ required_error: "ZIP code is required" })
        .regex(zipCodeRegex, "Invalid ZIP code format (e.g., 12345 or 12345-6789)"),
});

export function AddressInfo({ session }: { session: QuoteSession }) {
    const [lastResult, setLastResult] = useState<SubmissionResult<string[]> | null | undefined>()
    const { formData } = session;
    const [form, fields] = useForm({
        id: 'quote-address-info',
        constraint: getZodConstraint(AddressInfoSchema),
        lastResult: lastResult,
        defaultValue: {
            streetAddress: formData.streetAddress,
            secondaryAddress: formData.secondaryAddress,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
        },
        onValidate({ formData }) {
            const submission = parseWithZod(formData, { schema: AddressInfoSchema });
            return submission;
        }
    })

    return (
        <div className='container'>
            <div className='py-5 lg:py-10'>
                <h2 className='text-center max-w-[720px] mx-auto'>Step 3: Address Information</h2>
                <QuoteForm
                    action={QuoteForm.ACTIONS.PreviousStep}
                    className="absolute left-0 top-0 lg:left-12 lg:top-11"
                >
                    <Button variant='secondary' type="submit">
                        <Icon name="arrow-left" />
                    </Button>
                </QuoteForm>
            </div>
            <QuoteForm
                action={QuoteForm.ACTIONS.NextStep}
                formProps={{...getFormProps(form)}}
                className="max-w-96 mx-auto"
            >
                {(fetcher) => {
                    const fetchedData = fetcher.data;
                    if (fetchedData !== lastResult) {
                        setLastResult(fetchedData);
                    }

                    return (
                        <>
                            <HoneypotInputs />
                            <Field
                            labelProps={{ children: "Street Address" }}
                            inputProps={{
                                ...getInputProps(fields.streetAddress, { type: "text" }),
                                autoComplete: "address-line1",
                            }}
                            errors={fields.streetAddress.errors}
                            />
                            <Field
                            labelProps={{ children: "Street Address Line 2 (Optional)" }}
                            inputProps={{
                                ...getInputProps(fields.secondaryAddress, { type: "text" }),
                                autoComplete: "address-line2",
                            }}
                            errors={fields.secondaryAddress.errors}
                            />
                            <div className="grid md:grid-cols-3 gap-x-5">
                                <Field
                                labelProps={{ children: "City" }}
                                inputProps={{
                                    ...getInputProps(fields.city, { type: "text" }),
                                    autoComplete: "address-level2",
                                }}
                                errors={fields.city.errors}
                                />
                                <SelectField
                                labelProps={{ children: "State" }}
                                selectProps={{
                                    ...getSelectProps(fields.state, {  }),
                                }}
                                errors={fields.state.errors}
                                >
                                    {usStates.map((state) => (
                                        <SelectFieldItem value={state.abbreviation} key={state.name}>
                                            {state.name}({state.abbreviation})
                                        </SelectFieldItem>
                                    ))}
                                </SelectField>
                                <Field
                                labelProps={{ children: "ZIP Code" }}
                                inputProps={{
                                    ...getInputProps(fields.zip, { type: "text" }),
                                    autoComplete: "postal-code",
                                }}
                                errors={fields.zip.errors}
                                />
                            </div>
                            <StatusButton
                                type="submit"
                                status={fetcher.state !== 'idle' ? 'pending' : (form.status ?? 'idle')}
                                disabled={fetcher.state !== 'idle'}
                                className='mx-auto'
                            >
                                Next
                            </StatusButton>
                        </>
                    );
                }}
            </QuoteForm>
        </div>
    )
}